/**
 * "Create protected copy" in the asset drawer (Annexure H Part I 111).
 *
 * For a scanned PostgreSQL table: the operator sees EVERY column the scan saw.
 * A classified column keeps or changes its method (the classification's
 * default is preselected) or is excluded. A column the scan did not classify
 * may be protected or excluded too; left as "copy as is", it goes into the
 * copy with its values, so Create stays disabled until the operator confirms,
 * by name, that those columns hold no personal data. The controller refuses
 * any unprotected column that confirmation does not name. The operator names
 * the schema, and Discovery writes a new table there on the same server. The
 * job's progress is polled until it finishes. The original table is never
 * changed (protected-copy-service.ts).
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import {
  getProtectedCopy,
  getProtectedCopyPlan,
  listProtectedCopies,
  problemDetail,
  startProtectedCopy,
  type ColumnChoice,
  type ProtectedCopyJob,
  type ProtectedCopyPlan,
} from '@/lib/api';

export const PROTECTED_COPY_PERMISSION = 'discovery:protected_copy:write';
const POLL_MS = 1500;
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$]{0,62}$/;

/** A column's choice on the screen: a method, excluded, or copied as it is. */
export type PanelChoice = ColumnChoice | 'copy';

export const METHOD_HELP: Record<PanelChoice, string> = {
  mask: 'Masked: format kept, value gone. Not reversible.',
  tokenise: 'Tokenised: the same value always gives the same token, so joins still work. Not reversible from the copy.',
  pseudonymise: 'Pseudonymised: FOCTTA keeps the mapping; the DPO can reverse it, and every reversal is audited.',
  exclude: 'Excluded: the column is left out of the copy.',
  copy: 'Copied as is: every value goes into the copy unchanged. Only for a column that holds no personal data.',
};

/** Classified columns start on their default method; the others on "copy as is", which needs confirming. */
export function initialChoices(plan: Pick<ProtectedCopyPlan, 'columns' | 'unclassifiedColumns'>): Record<string, PanelChoice> {
  return Object.fromEntries([
    ...plan.columns.map((c) => [c.column, c.defaultMethod] as const),
    ...plan.unclassifiedColumns.map((c) => [c, 'copy'] as const),
  ]);
}

/** The columns that would go into the copy unprotected, in a stable order. */
export function unprotectedOf(choices: Record<string, PanelChoice>): string[] {
  return Object.entries(choices)
    .filter(([, m]) => m === 'copy')
    .map(([c]) => c)
    .sort((a, b) => a.localeCompare(b));
}

/** The request body's column half: methods for the rest, and the confirmed list. */
export function copyRequestOf(choices: Record<string, PanelChoice>): {
  methods: Record<string, ColumnChoice>;
  acknowledgedUnprotected: string[];
} {
  const methods: Record<string, ColumnChoice> = {};
  for (const [column, m] of Object.entries(choices)) {
    // Not methods[column] = m: a column named __proto__ would set the prototype.
    if (m !== 'copy') Object.defineProperty(methods, column, { value: m, enumerable: true, writable: true, configurable: true });
  }
  return { methods, acknowledgedUnprotected: unprotectedOf(choices) };
}

/** "1,200 of about 1,250 rows" while running; the total once done. */
export function progressLabel(job: Pick<ProtectedCopyJob, 'status' | 'rowsCopied' | 'rowsEstimated'>): string {
  const n = job.rowsCopied.toLocaleString('en-IN');
  if (job.status === 'completed') return `${n} rows copied`;
  // An abandoned job may have committed just before its process died, so a
  // failure does not promise that nothing was written; the error says which.
  if (job.status === 'failed') return 'Failed';
  return job.rowsEstimated ? `${n} of about ${job.rowsEstimated.toLocaleString('en-IN')} rows` : `${n} rows so far`;
}

function istTime(iso: string): string {
  return `${new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

function choiceOf(choices: Record<string, PanelChoice>, column: string, fallback: PanelChoice): PanelChoice {
  return Object.hasOwn(choices, column) ? (choices[column] as PanelChoice) : fallback;
}

const SELECT_CLASS = 'text-xs bg-background/50 border border-border/50 rounded-md px-1.5 py-1';

export function ProtectedCopyForm({
  plan,
  canCreate,
  choices,
  onChoice,
  confirmed,
  onConfirmed,
  targetSchema,
  onTargetSchema,
  targetTable,
  onTargetTable,
  busy,
  onSubmit,
}: {
  plan: ProtectedCopyPlan;
  canCreate: boolean;
  choices: Record<string, PanelChoice>;
  onChoice: (column: string, m: PanelChoice) => void;
  /** The operator ticked the confirmation for exactly the columns now copied as they are. */
  confirmed: boolean;
  onConfirmed: (v: boolean) => void;
  targetSchema: string;
  onTargetSchema: (v: string) => void;
  targetTable: string;
  onTargetTable: (v: string) => void;
  busy: boolean;
  onSubmit: () => void;
}) {
  if (plan.unavailableReason) {
    return <div className="text-xs text-muted-foreground" data-testid="protected-copy-unavailable">{plan.unavailableReason}</div>;
  }
  const valid = IDENTIFIER.test(targetSchema) && IDENTIFIER.test(targetTable);
  const unprotected = unprotectedOf(choices);
  const needsConfirmation = unprotected.length > 0 && !confirmed;
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">
        Writes a new table on the same server as {plan.source?.schema}.{plan.source?.table}. The original is not changed.
        The copy reads one snapshot of the table and may slow it while it runs. A schema change on that table (an ALTER
        TABLE, an index build) waits until the copy finishes, and reads and writes of the table queued behind that change
        wait too: run the copy when no schema change is planned.
      </div>
      <table className="w-full text-xs">
        <tbody>
          {plan.columns.map((c) => {
            const m = choiceOf(choices, c.column, c.defaultMethod);
            return (
              <tr key={c.column} className="border-t border-border/30">
                <td className="py-1 font-mono">{c.column}</td>
                <td className="py-1"><Badge variant="glass" className="text-[10px]">{c.label}</Badge></td>
                <td className="py-1 text-right">
                  <select
                    aria-label={`Method for ${c.column}`}
                    value={m}
                    onChange={(e) => onChoice(c.column, e.target.value as PanelChoice)}
                    title={METHOD_HELP[m]}
                    className={SELECT_CLASS}
                  >
                    <option value="mask">Mask</option>
                    <option value="tokenise">Tokenise</option>
                    <option value="pseudonymise">Pseudonymise</option>
                    <option value="exclude">Exclude from the copy</option>
                  </select>
                </td>
              </tr>
            );
          })}
          {plan.unclassifiedColumns.map((column) => {
            const m = choiceOf(choices, column, 'copy');
            return (
              <tr key={column} className="border-t border-border/30">
                <td className="py-1 font-mono">{column}</td>
                <td className="py-1"><Badge variant="outline" className="text-[10px]">not classified</Badge></td>
                <td className="py-1 text-right">
                  <select
                    aria-label={`Method for ${column}`}
                    value={m}
                    onChange={(e) => onChoice(column, e.target.value as PanelChoice)}
                    title={METHOD_HELP[m]}
                    className={SELECT_CLASS}
                  >
                    <option value="copy">Copy as is</option>
                    <option value="mask">Mask</option>
                    <option value="tokenise">Tokenise</option>
                    <option value="pseudonymise">Pseudonymise</option>
                    <option value="exclude">Exclude from the copy</option>
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {unprotected.length > 0 && (
        <label className="flex items-start gap-1.5 text-xs" data-testid="protected-copy-confirm">
          <input
            type="checkbox"
            aria-label="Confirm the unprotected columns hold no personal data"
            checked={confirmed}
            onChange={(e) => onConfirmed(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I confirm these columns hold no personal data: <span className="font-mono">{unprotected.join(', ')}</span>. They
            are copied as they are.
          </span>
        </label>
      )}
      <div className="flex items-center gap-2">
        <input
          aria-label="Target schema"
          value={targetSchema}
          onChange={(e) => onTargetSchema(e.target.value.trim())}
          placeholder="schema (must exist)"
          className="w-1/2 text-xs bg-background/50 border border-border/50 rounded-md px-2 py-1.5 font-mono"
        />
        <input
          aria-label="Target table"
          value={targetTable}
          onChange={(e) => onTargetTable(e.target.value.trim())}
          className="w-1/2 text-xs bg-background/50 border border-border/50 rounded-md px-2 py-1.5 font-mono"
        />
      </div>
      <Button
        size="sm"
        variant="glass"
        disabled={!canCreate || !valid || busy || needsConfirmation}
        onClick={onSubmit}
        data-testid="protected-copy-create"
      >
        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {busy ? 'Starting…' : 'Create protected copy'}
      </Button>
      {!canCreate && (
        <div className="text-[10px] text-muted-foreground">Only the DPO or the IT Admin can create a protected copy.</div>
      )}
    </div>
  );
}

export function ProtectedCopyJobRow({ job }: { job: ProtectedCopyJob }) {
  const pct = job.rowsEstimated ? Math.min(100, Math.round((job.rowsCopied / job.rowsEstimated) * 100)) : null;
  return (
    <div className="glass-lite rounded-md p-2 text-xs space-y-1" data-testid="protected-copy-job">
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate">{job.target.schema}.{job.target.table}</code>
        <Badge variant={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'critical' : 'warning'} className="text-[10px]">
          {job.status}
        </Badge>
      </div>
      {job.status === 'running' && pct !== null && (
        <div className="h-1 rounded bg-border/40"><div className="h-1 rounded bg-primary" style={{ width: `${pct}%` }} /></div>
      )}
      <div className="text-muted-foreground">{progressLabel(job)} · {istTime(job.createdAt)}</div>
      {job.error && <div className="text-sev-high">{job.error}</div>}
    </div>
  );
}

export function ProtectedCopyPanel({ assetId }: { assetId: string }) {
  const canCreate = useCan(PROTECTED_COPY_PERMISSION);
  const [plan, setPlan] = useState<ProtectedCopyPlan | null>(null);
  const [jobs, setJobs] = useState<ProtectedCopyJob[]>([]);
  const [choices, setChoices] = useState<Record<string, PanelChoice>>({});
  // The confirmation holds for the exact list it was given: changing which
  // columns are copied as they are clears it.
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  const [targetSchema, setTargetSchema] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPlan(null);
    setError(null);
    Promise.all([getProtectedCopyPlan(assetId), listProtectedCopies(assetId)])
      .then(([p, j]) => {
        if (!live) return;
        setPlan(p);
        setJobs(j);
        setChoices(initialChoices(p));
        setConfirmedFor(null);
        setTargetTable(p.defaultTarget?.table ?? '');
      })
      .catch((e: unknown) => live && setError(problemDetail(e, 'Could not load the protected copy plan')));
    return () => {
      live = false;
    };
  }, [assetId]);

  const unprotectedKey = unprotectedOf(choices).join('\n');
  const running = jobs.find((j) => j.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      getProtectedCopy(running.id)
        .then((j) => setJobs((all) => all.map((x) => (x.id === j.id ? j : x))))
        .catch(() => undefined);
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [running]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const job = await startProtectedCopy(assetId, { targetSchema, targetTable, ...copyRequestOf(choices) });
      setJobs((all) => [job, ...all]);
    } catch (e) {
      setError(problemDetail(e, 'The protected copy could not be started'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="protected-copy-panel">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Protected copy</div>
      {!plan && !error && (
        <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
      )}
      {plan && (
        <ProtectedCopyForm
          plan={plan}
          canCreate={canCreate && !running}
          choices={choices}
          onChoice={(c, m) => setChoices((all) => ({ ...all, [c]: m }))}
          confirmed={confirmedFor === unprotectedKey}
          onConfirmed={(v) => setConfirmedFor(v ? unprotectedKey : null)}
          targetSchema={targetSchema}
          onTargetSchema={setTargetSchema}
          targetTable={targetTable}
          onTargetTable={setTargetTable}
          busy={busy}
          onSubmit={() => void submit()}
        />
      )}
      {error && <div className="text-[10px] text-sev-high mt-1">{error}</div>}
      {jobs.length > 0 && <div className="mt-2 space-y-1.5">{jobs.map((j) => <ProtectedCopyJobRow key={j.id} job={j} />)}</div>}
    </div>
  );
}
